import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Play, Youtube } from "lucide-react";
import { YT_CHANNEL_URL, YT_UPLOADS_PLAYLIST, ytId, mediaUrl, videoThumb } from "../../data/site";
import { Reveal, Eyebrow } from "./Reveal";

const toTab = (v) => {
  const id = ytId(v.url);
  return { key: v.id, title: v.title || "Untitled", artist: v.artist || "Money Alwayz", thumb: videoThumb(v),
    src: id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0` : null, file: id ? null : mediaUrl(v.url) };
};

const ALL = { key: "all", title: "All uploads", artist: "Full channel playlist", thumb: "/studio.jpg",
  src: `https://www.youtube-nocookie.com/embed/videoseries?list=${YT_UPLOADS_PLAYLIST}&autoplay=1` };

const Player = ({ tab }) => tab.file ? (
  <video data-testid="video-player-file" className="absolute inset-0 h-full w-full bg-black" src={tab.file} controls autoPlay playsInline />
) : (
  <iframe data-testid="video-player-iframe" className="absolute inset-0 h-full w-full" src={tab.src} title={tab.title}
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
);

export const Videos = ({ videos = [], channelUrl = YT_CHANNEL_URL }) => {
  const tabs = useMemo(() => [...videos.map(toTab), ALL], [videos]);
  const [activeKey, setActiveKey] = useState(null);
  const [playing, setPlaying] = useState(false);
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];
  const pick = (t) => { setActiveKey(t.key); setPlaying(false); };
  return (
    <section id="videos" data-testid="videos-section" className="px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
      <div className="mx-auto max-w-7xl">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Eyebrow num="01">Videos</Eyebrow>
            <h2 className="mt-6 font-display text-5xl uppercase leading-[0.9] sm:text-7xl">Now <span className="font-serif-i normal-case text-[var(--cash)]">playing</span></h2>
          </div>
          <a data-testid="videos-subscribe-link" href={channelUrl} target="_blank" rel="noopener noreferrer"
            className="flex w-fit items-center gap-3 rounded-full bg-[#FF0033] px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-transform hover:scale-105">
            <Youtube size={18} /> Subscribe @kashaethegod
          </a>
        </Reveal>

        <Reveal delay={0.1} className="mt-12 grid gap-6 lg:grid-cols-12">
          <div className="relative aspect-video overflow-hidden rounded-[28px] border border-[var(--line)] bg-black lg:col-span-9">
            {playing ? <Player tab={active} /> : (
              <button data-testid="video-play-btn" onClick={() => setPlaying(true)} className="group absolute inset-0 h-full w-full">
                <img src={active.thumb} alt={active.title} className="h-full w-full object-cover opacity-70 transition-transform duration-[1.2s] group-hover:scale-105" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_20%,rgba(0,0,0,0.75))]" />
                <motion.span whileHover={{ scale: 1.1 }} className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--cash)] text-[var(--ink)] sm:h-28 sm:w-28">
                  <Play size={28} fill="currentColor" />
                </motion.span>
                <span className="absolute bottom-4 left-4 text-left sm:bottom-8 sm:left-8">
                  <span className="block font-mono text-xs uppercase tracking-[0.3em] text-[var(--cash)]">{active.artist}</span>
                  <span className="block font-display text-2xl uppercase sm:text-5xl">{active.title}</span>
                </span>
              </button>
            )}
          </div>
          <div data-lenis-prevent className="flex gap-4 overflow-x-auto lg:col-span-3 lg:max-h-[560px] lg:flex-col lg:overflow-y-auto">
            {tabs.map((t, i) => (
              <button key={t.key} data-testid={`video-tab-${t.key}`} onClick={() => pick(t)}
                className={`group min-w-[220px] shrink-0 rounded-2xl border p-3 text-left transition-colors lg:min-w-0 ${active.key === t.key ? "border-[var(--cash)] bg-[var(--ink-3)]" : "border-[var(--line)] hover:border-zinc-500"}`}>
                <div className="aspect-video overflow-hidden rounded-xl"><img src={t.thumb} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                <p className="mt-3 font-mono text-xs text-zinc-400">{String(i + 1).padStart(2, "0")}</p>
                <p className="font-display text-xl uppercase">{t.title}</p>
              </button>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
